import { useEffect, useState } from "react";
import {
    loadStopsWhitelist,
    peekStopsWhitelist,
} from "../Lib/Utils/stopsWhitelist";

/**
 * Stop codes that may be picked as an origin or a destination, or `null` while
 * the registry has not been read yet.
 *
 * The `null` is load-bearing and must be passed through rather than smoothed
 * into `[]`: the whitelist fails closed, so "not read yet" and "read, nothing
 * allowed" produce the same empty list but must look different to the picker —
 * one is a loading row, the other is an answer.
 *
 * Reads a warm cache synchronously on first render (ClientProvider warms the
 * registry at TOKEN_READY), so a picker opened any time after startup filters
 * correctly on first paint instead of flashing a loading row.
 *
 * Not one-shot, unlike useMinConnectionMinutes: that hook's consumers are
 * short-lived modals that mount long after the warm read, while the search
 * widget is mounted from the first paint of the landing page — before the
 * registry can possibly have landed. It has to swap in the value when it
 * arrives, or the From/To picker stays empty for the session.
 *
 * [refreshKey]: each time it becomes a new non-null value (the From or To
 * picker opens — including switching straight from one to the other), the
 * registry is re-read from the network, so a `stops_whitelist` change applies
 * on the next open instead of the next page load. The last known list stays
 * on screen until the fresh one lands (and stays if the read fails).
 */
export function useStopsWhitelist(refreshKey: string | null = null): string[] | null {
    const [whitelist, setWhitelist] = useState<string[] | null>(() =>
        peekStopsWhitelist()
    );

    useEffect(() => {
        if (refreshKey === null) return;
        let isMounted = true;
        loadStopsWhitelist({ forceRefresh: true }).then((codes) => {
            if (isMounted) setWhitelist(codes);
        });
        return () => {
            isMounted = false;
        };
    }, [refreshKey]);

    useEffect(() => {
        if (whitelist !== null) return;
        let isMounted = true;
        loadStopsWhitelist().then((codes) => {
            if (isMounted) setWhitelist(codes);
        });
        return () => {
            isMounted = false;
        };
    }, [whitelist]);

    return whitelist;
}
